import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import {
  AlertCircle,
  ArrowUpRight,
  Bell,
  Clock3,
  FileText,
  Lock,
  Mic,
  MousePointerClick,
  ShieldCheck,
  Sparkles,
  TriangleAlert,
} from "lucide-react";
import { useState } from "react";
import { Link, Outlet, useLocation } from "react-router-dom";
import { PageContainer } from "@/components/page-container";
import { PageHeader } from "@/components/page-header";
import { ListSkeleton } from "@/components/skeletons";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyTitle,
} from "@/components/ui/empty";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Heading, headingVariants, Text } from "@/components/ui/typography";
import {
  CORE_PLAN_DISPLAY,
  describeBillingLabel,
} from "@/lib/subscription-display";
import { cn } from "@/lib/utils";
import { trpc, withErrorToast } from "@/utils/trpc";

const ALL_SITES_VALUE = "all-sites";
const GENERAL_NOTE_VALUE = "general";
const CORE_FEATURES = [
  {
    icon: Mic,
    label: "Voice notes with transcription",
  },
  {
    icon: ShieldCheck,
    label: "Certificate expiry alerts",
  },
  {
    icon: FileText,
    label: "PDF report export",
  },
  {
    icon: Sparkles,
    label: "Automated daily site summaries",
  },
  {
    icon: TriangleAlert,
    label: "Missing document risk flags",
  },
  {
    icon: Bell,
    label: "Notifications and reminders",
  },
  {
    icon: Clock3,
    label: "Unlimited report history",
  },
] as const;

function formatDurationBadge(seconds: number | null | undefined): string {
  if (seconds == null) return "—";
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;

  if (minutes === 0) {
    return `${remainingSeconds}s`;
  }

  return `${minutes}m ${String(remainingSeconds).padStart(2, "0")}s`;
}

function statusBadgeVariant(status: string) {
  if (status === "ready" || status === "approved") return "gray";
  if (status === "failed" || status === "rejected") return "destructive";
  return "secondary";
}

function matchesSiteFilter(siteId: string | null, selectedSiteFilter: string) {
  if (selectedSiteFilter === ALL_SITES_VALUE) {
    return true;
  }

  if (selectedSiteFilter === GENERAL_NOTE_VALUE) {
    return siteId === null;
  }

  return siteId === selectedSiteFilter;
}

function RecordingsListCard({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <Card className={className}>
      <CardContent>{children}</CardContent>
    </Card>
  );
}

function VoiceNotesUpgradeCard({ message }: { message: string }) {
  const coreBillingLabel = describeBillingLabel("core", "monthly");

  return (
    <div className="grid">
      <div
        aria-hidden="true"
        className="col-start-1 row-start-1 pointer-events-none select-none"
      >
        <div className="lg:hidden">
          <LockedNotesListSkeleton />
        </div>

        <div className="hidden lg:grid lg:grid-cols-[minmax(320px,0.9fr)_minmax(0,1.4fr)] lg:items-start lg:gap-6">
          <LockedNotesListSkeleton />
          <div className="sticky top-24">
            <LockedNoteDetailSkeleton />
          </div>
        </div>
      </div>

      <div className="col-start-1 row-start-1 rounded-xl bg-background/55 backdrop-blur-[3px]" />

      <div className="col-start-1 row-start-1 z-10 flex items-center justify-center px-4 py-6 sm:px-6 lg:px-12 lg:py-12">
        <RecordingsListCard className="w-full max-w-5xl shadow-[var(--surface-shadow)]">
          <div className="overflow-hidden rounded-xl border border-border bg-card lg:grid lg:grid-cols-[minmax(0,0.88fr)_minmax(0,1fr)]">
            <div className="flex min-w-0 flex-col justify-between gap-8 bg-card-foreground px-6 py-8 text-background sm:px-8 sm:py-12 lg:min-h-[32rem] lg:px-12">
              <div className="space-y-6">
                <Badge
                  variant="outline"
                  className="border-primary/25 bg-primary/10 text-primary"
                >
                  {CORE_PLAN_DISPLAY.badgeLabel}
                </Badge>

                <div className="space-y-4">
                  <Heading
                    variant="page"
                    as="h2"
                    className="max-w-sm text-background"
                  >
                    Core compliance features.
                  </Heading>
                  <div>
                    <span
                      className={cn(
                        headingVariants({ variant: "display" }),
                        "text-background"
                      )}
                    >
                      {CORE_PLAN_DISPLAY.heroPriceDisplay}
                    </span>
                    <span className="ml-2 align-baseline text-sm font-medium text-background/60">
                      / {coreBillingLabel}
                    </span>
                  </div>
                </div>

                <div className="max-w-md space-y-3">
                  <p className="text-sm font-medium text-background">
                    {message}
                  </p>
                  <p className="text-sm leading-6 text-background/70">
                    Voice Notes captures toolbox talks, handovers, and field
                    updates with transcription and summaries ready for the team.
                  </p>
                </div>
              </div>

              <Button asChild size="lg" className="w-full sm:w-fit">
                <Link to="/billing">
                  Upgrade to Core
                  <ArrowUpRight className="size-4" />
                </Link>
              </Button>
            </div>

            <div className="min-w-0 bg-card">
              <div className="border-b border-border/60 px-6 py-6 sm:px-8 lg:px-12">
                <p className="text-sm font-medium text-foreground">
                  Included in Core
                </p>
              </div>

              <div>
                {CORE_FEATURES.map(({ icon: Icon, label }, index) => (
                  <div
                    key={label}
                    className={cn(
                      "flex items-center gap-4 px-6 py-4 sm:px-8 lg:px-12",
                      index !== 0 && "border-t border-border/60"
                    )}
                  >
                    <Icon className="size-5 shrink-0 text-muted-foreground" />
                    <span className="text-sm text-foreground">{label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </RecordingsListCard>
      </div>
    </div>
  );
}

function LockedNotesListSkeleton() {
  return (
    <RecordingsListCard className="opacity-70">
      <div className="space-y-4 py-4">
        <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div className="space-y-2">
            <SkeletonLine className="h-4 w-24" />
            <SkeletonLine className="h-3.5 w-56 max-w-full" />
          </div>
          <div className="h-9 w-full rounded-sm border border-border/60 bg-background/80 sm:w-56" />
        </div>

        <div className="space-y-3">
          <LockedRecordingListItem />
          <LockedRecordingListItem isSelected />
          <LockedRecordingListItem />
          <LockedRecordingListItem />
        </div>
      </div>
    </RecordingsListCard>
  );
}

function LockedRecordingListItem({
  isSelected = false,
}: {
  isSelected?: boolean;
}) {
  return (
    <div
      className={cn(
        "rounded-lg border border-border/60 bg-card p-4 shadow-sm",
        isSelected &&
          "border-foreground/15 bg-muted/60 shadow-lg ring-2 ring-inset ring-foreground/10"
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1 space-y-2">
          <SkeletonLine className="h-4 w-40 max-w-full" />
          <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
            <SkeletonLine className="h-3.5 w-20" />
            <SkeletonLine className="h-3.5 w-24" />
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-2 self-start">
          <SkeletonPill className="w-14" />
          <SkeletonPill className="w-16" />
        </div>
      </div>
    </div>
  );
}

function LockedNoteDetailSkeleton() {
  return (
    <div className="space-y-4 opacity-70">
      <div className="rounded-lg border border-border/60 bg-card p-4 shadow-sm">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <SkeletonLine className="h-7 w-64 max-w-full" />
            <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-2">
              <SkeletonLine className="h-3.5 w-36" />
              <SkeletonLine className="h-3.5 w-14" />
            </div>
            <div className="mt-4 max-w-xs space-y-2">
              <SkeletonLine className="h-3 w-12" />
              <div className="h-10 rounded-sm border border-border/60 bg-background/80" />
            </div>
          </div>

          <div className="flex shrink-0 items-center gap-2">
            <SkeletonPill className="w-16" />
            <div className="flex size-9 items-center justify-center rounded-sm border border-border/60 bg-background/80">
              <Lock className="size-4 text-muted-foreground" />
            </div>
          </div>
        </div>
      </div>

      <div className="rounded-lg border border-border/60 bg-card p-4 shadow-sm">
        <div className="h-10 rounded-lg border border-border/60 bg-background/80" />
      </div>

      <div className="rounded-lg border border-border/60 bg-card shadow-sm">
        <div className="border-b border-border/60 px-6 py-4">
          <SkeletonLine className="h-4 w-20" />
        </div>
        <div className="space-y-2 px-6 py-6">
          <SkeletonLine className="h-3.5 w-full" />
          <SkeletonLine className="h-3.5 w-5/6" />
          <SkeletonLine className="h-3.5 w-2/3" />
        </div>
      </div>

      <div className="rounded-lg border border-border/60 bg-card shadow-sm">
        <div className="flex items-center justify-between gap-3 border-b border-border/60 px-6 py-4">
          <SkeletonLine className="h-4 w-24" />
          <SkeletonPill className="w-32" />
        </div>
        <div className="space-y-2 px-6 py-6">
          <SkeletonLine className="h-3.5 w-full" />
          <SkeletonLine className="h-3.5 w-full" />
          <SkeletonLine className="h-3.5 w-4/5" />
          <SkeletonLine className="h-3.5 w-11/12" />
          <SkeletonLine className="h-3.5 w-2/3" />
        </div>
      </div>
    </div>
  );
}

function SkeletonLine({ className }: { className?: string }) {
  return <div className={cn("rounded-sm bg-border", className)} />;
}

function SkeletonPill({ className }: { className?: string }) {
  return (
    <div
      className={cn(
        "h-6 rounded-xs border border-border/60 bg-background/80",
        className
      )}
    />
  );
}

function RecordingListItem({
  recording,
  isSelected,
  siteLabel,
}: {
  recording: {
    id: string;
    title: string;
    siteId: string | null;
    createdAt: string | Date | null;
    audioDuration: number | null;
    status: string;
  };
  isSelected: boolean;
  siteLabel: string;
}) {
  return (
    <Link
      to={recording.id}
      className={cn(
        "block rounded-lg border border-border/60 bg-card p-4 shadow-sm transition-[background-color,box-shadow,border-color,transform] hover:bg-muted/30 hover:shadow-md",
        isSelected &&
          "border-foreground/15 bg-muted/70 shadow-lg ring-2 ring-inset ring-foreground/15"
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 space-y-2">
          <p className="truncate font-medium text-foreground">
            {recording.title}
          </p>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-muted-foreground">
            <span>{siteLabel}</span>
            {recording.createdAt ? (
              <span>{format(new Date(recording.createdAt), "PP")}</span>
            ) : null}
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-2 self-start">
          <Text variant="meta" as="span" className="shrink-0 text-xs">
            {formatDurationBadge(recording.audioDuration)}
          </Text>
          <Badge
            variant={statusBadgeVariant(recording.status)}
            className="shrink-0"
          >
            {recording.status === "pending_approval"
              ? "pending approval"
              : recording.status}
          </Badge>
        </div>
      </div>
    </Link>
  );
}

function RecordingsList({
  selectedSiteFilter,
  onSiteFilterChange,
}: {
  selectedSiteFilter: string;
  onSiteFilterChange: (value: string) => void;
}) {
  const { pathname } = useLocation();
  const selectedId = pathname.startsWith("/notes/")
    ? pathname.split("/notes/")[1]
    : undefined;

  const {
    data: recordings,
    isLoading,
    isError,
  } = useQuery({
    ...withErrorToast(trpc.recordings.listRecordings.queryOptions({})),
  });
  const managedSitesQuery = useQuery({
    ...trpc.getManagedSites.queryOptions(),
  });
  const managedSites = managedSitesQuery.data ?? [];

  const siteNameById = new Map(
    managedSites.map((site) => [site.id, site.name])
  );
  const filteredRecordings = recordings?.filter((recording) =>
    matchesSiteFilter(recording.siteId, selectedSiteFilter)
  );

  if (isLoading) {
    return (
      <RecordingsListCard>
        <ListSkeleton rows={4} />
      </RecordingsListCard>
    );
  }

  if (isError) {
    return (
      <RecordingsListCard>
        <Alert variant="destructive">
          <AlertCircle />
          <AlertTitle>Failed to load recordings</AlertTitle>
          <AlertDescription>
            There was a problem loading your voice notes. Please try again.
          </AlertDescription>
        </Alert>
      </RecordingsListCard>
    );
  }

  if (!recordings?.length) {
    return (
      <RecordingsListCard>
        <Empty>
          <EmptyHeader>
            <EmptyTitle>No recordings yet</EmptyTitle>
            <EmptyDescription>
              Recordings made on the iOS app will appear here.
            </EmptyDescription>
          </EmptyHeader>
        </Empty>
      </RecordingsListCard>
    );
  }

  return (
    <RecordingsListCard>
      <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="text-sm font-medium text-foreground">Voice Notes</p>
          <p className="text-sm text-muted-foreground">
            Filter notes by site or keep general notes in view.
          </p>
        </div>
        <Select value={selectedSiteFilter} onValueChange={onSiteFilterChange}>
          <SelectTrigger
            size="sm"
            className="w-full sm:w-56"
            aria-label="Filter notes by site"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_SITES_VALUE}>All notes</SelectItem>
            <SelectItem value={GENERAL_NOTE_VALUE}>General notes</SelectItem>
            {managedSites.map((site) => (
              <SelectItem key={site.id} value={site.id}>
                {site.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {filteredRecordings && filteredRecordings.length > 0 ? (
        <div className="space-y-3">
          {filteredRecordings.map((recording) => (
            <RecordingListItem
              key={recording.id}
              recording={recording}
              isSelected={selectedId === recording.id}
              siteLabel={
                recording.siteId
                  ? (siteNameById.get(recording.siteId) ?? recording.siteId)
                  : "General"
              }
            />
          ))}
        </div>
      ) : (
        <Empty>
          <EmptyHeader>
            <EmptyTitle>No notes match this filter</EmptyTitle>
            <EmptyDescription>
              Try another site or switch back to all notes.
            </EmptyDescription>
          </EmptyHeader>
        </Empty>
      )}
    </RecordingsListCard>
  );
}

function DesktopDetailPlaceholder() {
  return (
    <div className="flex flex-col items-center justify-center gap-2 rounded-lg border border-dashed px-6 py-16 text-center text-muted-foreground">
      <MousePointerClick className="size-6 opacity-40" />
      <p className="text-sm">Select a recording to view details</p>
    </div>
  );
}

export default function NotesIndex() {
  const { pathname } = useLocation();
  const isDetail = pathname !== "/notes" && pathname.startsWith("/notes/");
  const [selectedSiteFilter, setSelectedSiteFilter] = useState(ALL_SITES_VALUE);
  const capabilitiesQuery = useQuery({
    ...trpc.recordings.getCapabilities.queryOptions({}),
  });
  const upgradeMessage =
    capabilitiesQuery.data?.upgradeMessage ??
    "Upgrade to Core to use voice notes.";
  const canUseVoiceNotes = capabilitiesQuery.data?.canUseVoiceNotes ?? false;

  return (
    <PageContainer className="space-y-6">
      <PageHeader
        title="Notes"
        description="Voice recordings, toolbox talks, and field notes with AI transcription."
      />

      {capabilitiesQuery.isLoading ? (
        <RecordingsListCard>
          <ListSkeleton rows={4} />
        </RecordingsListCard>
      ) : capabilitiesQuery.error ? (
        <RecordingsListCard>
          <p className="py-4 text-sm text-muted-foreground">
            {capabilitiesQuery.error instanceof Error
              ? capabilitiesQuery.error.message
              : "Failed to load voice notes."}
          </p>
        </RecordingsListCard>
      ) : !canUseVoiceNotes ? (
        <VoiceNotesUpgradeCard message={upgradeMessage} />
      ) : (
        <>
          {/* Mobile: show list OR detail */}
          <div className="lg:hidden">
            {isDetail ? (
              <Outlet />
            ) : (
              <RecordingsList
                selectedSiteFilter={selectedSiteFilter}
                onSiteFilterChange={setSelectedSiteFilter}
              />
            )}
          </div>

          {/* Desktop: side-by-side */}
          <div className="hidden lg:grid lg:grid-cols-[minmax(320px,0.9fr)_minmax(0,1.4fr)] lg:items-start lg:gap-6">
            <RecordingsList
              selectedSiteFilter={selectedSiteFilter}
              onSiteFilterChange={setSelectedSiteFilter}
            />
            <div className="sticky top-24">
              {isDetail ? <Outlet /> : <DesktopDetailPlaceholder />}
            </div>
          </div>
        </>
      )}
    </PageContainer>
  );
}
